import { useMemo, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import messages from "messages";
// styles
import styles from "./styles.module.scss";
// components
import { ReactComponent as OutlinePrint } from "assets/svg/outline-print.svg";
import { ReactComponent as OutlineDownload } from "assets/svg/outline-download.svg";
import { ReactComponent as OutlineCross } from "assets/svg/outline-cross.svg";
import IconButton from "components/icon-button";
import BillingViewerPdf from "components/Dashboard/components/billing-viewer-pdf";
// contexts
import { useTimeOnPageContext } from "contexts/time-on-page";
// utils
import { apiHostAndPath } from "utils/api";
import { AmplitudePageName, track } from "utils/amplitude";
import downloadFromDifferentOrigin from "utils/downloadFromDifferentOrigin";
// constants
import { RECEIPT_TYPES } from "components/Dashboard/DoctorDashboard/patient-charts/constants/receiptTypes";
import queryAppointment from "services/queryAppointment";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "view-receipt-page";

const ReceiptViewer = () => {
  const { setPage } = useTimeOnPageContext();
  useEffect(() => {
    setPage(AMPLITUDE_PAGE_NAME);
  }, [setPage]);

  const { receipt_type, timeslot_id } = useParams<{
    receipt_type: typeof RECEIPT_TYPES[keyof typeof RECEIPT_TYPES];
    timeslot_id: string;
  }>();

  const { practiceOwnerOrchidUserId } = useTeamContext();

  const [appointment, setAppointment] = useState<AppointmentMetaData>();

  useEffect(() => {
    if (!timeslot_id) return;
    queryAppointment(parseInt(timeslot_id), practiceOwnerOrchidUserId)
      .then(setAppointment)
      .catch(console.error);
  }, [timeslot_id, practiceOwnerOrchidUserId]);

  const fileUrl = useMemo(
    () =>
      apiHostAndPath(
        `/api/scheduler/v1/invoice/${receipt_type}/${timeslot_id}${
          practiceOwnerOrchidUserId
            ? `?on_behalf_of=${practiceOwnerOrchidUserId}`
            : ""
        }`,
      ),
    [receipt_type, timeslot_id, practiceOwnerOrchidUserId],
  );

  const onDownload = useCallback(() => {
    downloadFromDifferentOrigin(fileUrl, "receipt.pdf").catch(console.error);
  }, [fileUrl]);

  return (
    <div className={styles["receipt-viewer"]}>
      <div className={styles["receipt-viewer__actions"]}>
        <div>
          <IconButton
            className={styles["billing-viewer-title__close"]}
            onClick={() => {
              track(AMPLITUDE_PAGE_NAME, "exit-button", "click");
              window.close();
            }}
          >
            <OutlineCross />
          </IconButton>
          {!appointment?.patient ? null : (
            <span className={styles["billing-viewer-title__contact"]}>
              {messages.receiptFor +
                " " +
                appointment.patient.first_name +
                " " +
                appointment.patient.last_name}
            </span>
          )}
        </div>

        <div className={styles["receipt-viewer__right-actions"]}>
          <IconButton
            className={styles["receipt-viewer__icon"]}
            as="a"
            target="print"
            rel="noopener noreferrer"
            href={fileUrl}
            onClick={() => {
              // open as popup
              track(AMPLITUDE_PAGE_NAME, "print-button", "click");
              window.open(fileUrl, "print", "popup");
              return false;
            }}
          >
            <OutlinePrint />
          </IconButton>
          <IconButton
            className={styles["receipt-viewer__icon"]}
            onClick={() => {
              track(AMPLITUDE_PAGE_NAME, "download-button", "click");
              onDownload();
            }}
          >
            <OutlineDownload />
          </IconButton>
        </div>
      </div>
      <BillingViewerPdf fileUrl={fileUrl} />
    </div>
  );
};

export default ReceiptViewer;
